<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('markdown.title') }}</h1>
      <p>{{ $t('markdown.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="toolbar">
        <button class="tool-btn" @click="insertText('**', '**')" :title="$t('markdown.bold')">B</button>
        <button class="tool-btn italic" @click="insertText('*', '*')" :title="$t('markdown.italic')">I</button>
        <button class="tool-btn" @click="insertText('# ')" :title="$t('markdown.heading')">H</button>
        <button class="tool-btn" @click="insertText('- ')" :title="$t('markdown.list')">☰</button>
        <button class="tool-btn" @click="insertText('1. ')" :title="$t('markdown.orderedList')">1.</button>
        <button class="tool-btn" @click="insertText('[', '](url)')" :title="$t('markdown.link')">🔗</button>
        <button class="tool-btn" @click="insertText('![alt](', ')')" :title="$t('markdown.image')">🖼️</button>
        <button class="tool-btn" @click="insertText('`', '`')" :title="$t('markdown.inlineCode')">`</button>
        <button class="tool-btn" @click="insertText('```\n', '\n```')" :title="$t('markdown.codeBlock')">{ }</button>
        <button class="tool-btn" @click="insertText('> ')" :title="$t('markdown.quote')">❝</button>
        <button class="tool-btn" @click="insertText('---\n')" :title="$t('markdown.divider')">―</button>
        <div class="toolbar-right">
          <button class="btn" @click="copyMarkdown">{{ $t('markdown.copyMarkdown') }}</button>
          <button class="btn" @click="copyHtml">{{ $t('markdown.copyHtml') }}</button>
          <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
        </div>
      </div>

      <div v-if="copyMessage" class="copy-toast">{{ copyMessage }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('markdown.inputLabel') }}</label>
          <textarea
            ref="editor"
            v-model="markdown"
            :placeholder="$t('markdown.inputPlaceholder')"
            spellcheck="false"
          ></textarea>
        </div>

        <div class="preview-panel">
          <label>{{ $t('markdown.previewLabel') }}</label>
          <div class="preview-content" v-html="compiledMarkdown"></div>
        </div>
      </div>

      <div class="syntax-help">
        <details>
          <summary>{{ $t('markdown.syntaxHelp') }}</summary>
          <div class="help-content">
            <code># Heading 1</code> <code>## Heading 2</code> <code>### Heading 3</code><br>
            <code>**Bold**</code> <code>*Italic*</code> <code>~~Strikethrough~~</code><br>
            <code>[Link](url)</code> <code>![Image](url)</code><br>
            <code>`Inline code`</code> <code>```Code block```</code><br>
            <code>- Unordered list</code> <code>1. Ordered list</code><br>
            <code>> Quote</code> <code>--- Divider</code>
          </div>
        </details>
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('markdown.whatIsMarkdown') }}</h2>
      <p>{{ $t('markdown.whatIsMarkdownDesc') }}</p>

      <h2>{{ $t('markdown.markdownAdvantages') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('markdown.markdownAdvantagesItems')" :key="'adv'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('markdown.markdownSyntaxGuide') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('markdown.markdownSyntaxGuideItems')" :key="'syntax'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('markdown.markdownUseCases') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('markdown.markdownUseCasesItems')" :key="'usecase'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('markdown.markdownTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('markdown.markdownTipsItems')" :key="'tip'+index">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MarkdownEditor',
  data() {
    return {
      markdown: `# 欢迎使用 Markdown 编辑器

这是一个**实时预览**的 Markdown 编辑器。

## 功能特点

- 实时预览
- 支持常用语法
- 一键复制

### 代码示例

\`\`\`javascript
function hello() {
  console.log('Hello, World!');
}
\`\`\`

> 这是一段引用文字

[访问链接](https://example.com)
`,
      copyMessage: ''
    }
  },
  computed: {
    compiledMarkdown() {
      return this.parseMarkdown(this.markdown)
    }
  },
  methods: {
    parseMarkdown(text) {
      if (!text) return ''
      
      let html = text
        // 转义HTML
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      
      // 代码块 (需要在其他规则之前处理)
      html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
        return `<pre><code class="language-${lang}">${code.trim()}</code></pre>`
      })
      
      // 行内代码
      html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
      
      // 标题
      html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>')
      html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>')
      html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>')
      
      // 粗体和斜体
      html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
      html = html.replace(/~~(.+?)~~/g, '<del>$1</del>')
      
      // 链接和图片
      html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%">')
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
      
      // 引用
      html = html.replace(/^&gt; (.*$)/gm, '<blockquote>$1</blockquote>')
      
      // 分割线
      html = html.replace(/^---$/gm, '<hr>')
      
      // 无序列表
      html = html.replace(/^- (.*$)/gm, '<li>$1</li>')
      html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
      
      // 有序列表
      html = html.replace(/^\d+\. (.*$)/gm, '<li>$1</li>')
      
      // 段落
      html = html.replace(/\n\n/g, '</p><p>')
      html = '<p>' + html + '</p>'
      
      // 清理空段落
      html = html.replace(/<p><\/p>/g, '')
      html = html.replace(/<p>(<h[1-6]>)/g, '$1')
      html = html.replace(/(<\/h[1-6]>)<\/p>/g, '$1')
      html = html.replace(/<p>(<ul>)/g, '$1')
      html = html.replace(/(<\/ul>)<\/p>/g, '$1')
      html = html.replace(/<p>(<pre>)/g, '$1')
      html = html.replace(/(<\/pre>)<\/p>/g, '$1')
      html = html.replace(/<p>(<blockquote>)/g, '$1')
      html = html.replace(/(<\/blockquote>)<\/p>/g, '$1')
      html = html.replace(/<p>(<hr>)<\/p>/g, '$1')
      
      return html
    },

    insertText(before, after = '') {
      const textarea = this.$refs.editor
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selected = this.markdown.substring(start, end)
      const replacement = before + selected + after
      
      this.markdown = this.markdown.substring(0, start) + replacement + this.markdown.substring(end)
      
      this.$nextTick(() => {
        textarea.focus()
        const newPos = start + before.length + selected.length
        textarea.setSelectionRange(newPos, newPos)
      })
    },

    async copyMarkdown() {
      await this.copyToClipboard(this.markdown)
      this.showCopyMessage(this.$t('markdown.markdownCopied'))
    },

    async copyHtml() {
      await this.copyToClipboard(this.compiledMarkdown)
      this.showCopyMessage(this.$t('markdown.htmlCopied'))
    },

    async copyToClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = text
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }
      } catch (e) {
        console.error('Copy failed:', e)
      }
    },

    showCopyMessage(message) {
      this.copyMessage = message
      setTimeout(() => {
        this.copyMessage = ''
      }, 2000)
    },

    clearAll() {
      this.markdown = ''
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 1400px;
  margin: 0 auto;
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
}

.tool-btn {
  width: 36px;
  height: 36px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  transition: all 0.2s;
}

.tool-btn:hover {
  background: var(--accent-light);
  border-color: var(--accent-color);
}

.tool-btn.italic {
  font-style: italic;
}

.toolbar-right {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.btn:hover {
  background: var(--bg-hover);
}

.copy-toast {
  position: fixed;
  top: 80px;
  right: 20px;
  background: var(--success-color);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: var(--shadow-lg);
  z-index: 1000;
}

.editor-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  min-height: 500px;
}

@media (max-width: 900px) {
  .editor-container {
    grid-template-columns: 1fr;
  }
}

.editor-panel, .preview-panel {
  display: flex;
  flex-direction: column;
}

.editor-panel label, .preview-panel label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.editor-panel textarea {
  flex: 1;
  min-height: 500px;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  color: var(--text-primary);
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: none;
}

.editor-panel textarea:focus {
  outline: none;
  border-color: var(--accent-color);
}

.preview-content {
  flex: 1;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  overflow-y: auto;
  color: var(--text-primary);
  line-height: 1.8;
}

.preview-content :deep(h1) {
  font-size: 28px;
  margin: 16px 0 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.preview-content :deep(h2) {
  font-size: 24px;
  margin: 14px 0 10px;
}

.preview-content :deep(h3) {
  font-size: 20px;
  margin: 12px 0 8px;
}

.preview-content :deep(p) {
  margin: 10px 0;
}

.preview-content :deep(code) {
  background: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px;
}

.preview-content :deep(pre) {
  background: var(--bg-tertiary);
  padding: 16px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 12px 0;
}

.preview-content :deep(pre code) {
  background: none;
  padding: 0;
}

.preview-content :deep(blockquote) {
  border-left: 4px solid var(--accent-color);
  padding-left: 16px;
  margin: 12px 0;
  color: var(--text-secondary);
}

.preview-content :deep(ul) {
  padding-left: 24px;
  margin: 10px 0;
}

.preview-content :deep(li) {
  margin: 6px 0;
}

.preview-content :deep(a) {
  color: var(--accent-color);
}

.preview-content :deep(hr) {
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 20px 0;
}

.syntax-help {
  margin-top: 16px;
}

.syntax-help summary {
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 14px;
}

.help-content {
  margin-top: 12px;
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  font-size: 13px;
  line-height: 2;
}

.help-content code {
  background: var(--bg-input);
  padding: 2px 8px;
  border-radius: 4px;
  margin-right: 8px;
}

.tool-description {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  margin-top: 32px;
  box-shadow: var(--shadow-md);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 12px;
}

.tool-description ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.tool-description li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}
</style>
